/**
 * NgwEditorScrollObserver — editorDOM 의 scroll 이벤트 + ResizeObserver 결합 헬퍼 (PR-N6).
 *
 * 책임 (pr-n0-inventory §6.2):
 *   - `editorDOM.addEventListener('scroll')` → `handler.onScrollChanged?(top, bottom)`
 *   - `ResizeObserver(editorDOM)` → `handler.editorHeightChanged?(scrollHeight, diff)`
 *
 * `top` 은 `scrollTop`, `bottom` 은 `scrollTop + clientHeight`. TinyMCE 평행의 `setScrollPos` 가
 * iframe 내부 viewport 좌표를 (top, bottom) 으로 전달하던 것과 의미축 동일.
 *
 * **cleanup 보장 (CLAUDE.md)**: `dispose()` 는 listener removeEventListener +
 * ResizeObserver.disconnect 를 모두 수행. `NgwEditorLifecycle.dispose()` 가 root.unmount 직전에
 * 호출.
 *
 * ResizeObserver 미지원 환경 (legacy jsdom) 은 silent skip — scroll 이벤트만 동작.
 */

import type { NgwEditorAdapter } from './NgwEditorAdapter'

export class NgwEditorScrollObserver {
  private readonly adapter: NgwEditorAdapter
  private target: HTMLElement | null = null
  private scrollListener: ((event: Event) => void) | null = null
  private resizeObserver: ResizeObserver | null = null
  private lastScrollHeight = 0

  constructor(adapter: NgwEditorAdapter) {
    this.adapter = adapter
  }

  attach(target: HTMLElement): void {
    if (this.target != null) {
      this.detach()
    }

    this.target = target
    this.lastScrollHeight = target.scrollHeight

    const listener = (): void => {
      const handler = this.adapter.getHandler()
      if (handler?.onScrollChanged == null) {
        return
      }
      const top = target.scrollTop
      const bottom = top + target.clientHeight
      handler.onScrollChanged(top, bottom)
    }
    this.scrollListener = listener
    target.addEventListener('scroll', listener)

    const win = this.getSafeWindow(target)
    if (win != null && typeof win.ResizeObserver === 'function') {
      const observer = new win.ResizeObserver(() => {
        const current = target.scrollHeight
        const diff = current - this.lastScrollHeight
        this.lastScrollHeight = current
        const handler = this.adapter.getHandler()
        handler?.editorHeightChanged?.(current, diff)
      })
      observer.observe(target)
      this.resizeObserver = observer
    }
  }

  detach(): void {
    if (this.target != null && this.scrollListener != null) {
      this.target.removeEventListener('scroll', this.scrollListener)
    }
    this.scrollListener = null

    if (this.resizeObserver != null) {
      this.resizeObserver.disconnect()
      this.resizeObserver = null
    }

    this.target = null
    this.lastScrollHeight = 0
  }

  private getSafeWindow(target: HTMLElement): (Window & typeof globalThis) | null {
    const doc = target.ownerDocument
    return doc?.defaultView ?? null
  }
}
